// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, Dropdown, TextInput, TextArea, useToast } from '@loa/ui-components';
import axios from 'axios';
import { proposalApi } from '../../api/proposalApi';
import {
  CONTRIBUTION_FREQUENCY_OPTIONS,
  PENSION_TYPE_OPTIONS,
  type Proposal,
} from '../../types/proposal';
import type { Client } from '../../types/client';
import { useSoloClient } from '../../hooks/useSoloClient';

type NewPensionProposalModalProps = Readonly<{
  open: boolean;
  onClose: () => void;
  clients: Client[];
  brokerageId: string;
  profileId: string;
  onCreated?: (proposal: Proposal) => void;
  existingProposal?: Proposal;
  onUpdated?: (proposal: Proposal) => void;
}>;

/**
 * Compose (or edit) a PENSION proposal. In edit mode the target client is
 * fixed (you cannot retarget after creation); the rest of the fields prefill
 * from the existing proposal's detail view.
 */
export const NewPensionProposalModal: React.FC<NewPensionProposalModalProps> = ({
  open,
  onClose,
  clients,
  profileId,
  onCreated,
  existingProposal,
  onUpdated,
}) => {
  const navigate = useNavigate();
  const toast = useToast();
  const isEditMode = Boolean(existingProposal);
  const detail = (existingProposal?.details ?? {}) as Record<string, unknown>;
  const { isSolo, soloClient } = useSoloClient(clients);

  const [targetClientId, setTargetClientId] = useState(existingProposal?.targetClientId ?? '');

  // Solo relationships (Investment & Protection Proposals PRD RQ-9) never
  // show the Client selector — auto-select the only possible recipient.
  useEffect(() => {
    if (!isEditMode && !targetClientId && isSolo && soloClient) {
      setTargetClientId(soloClient.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, isSolo, soloClient]);
  const [title, setTitle] = useState(existingProposal?.title ?? '');
  const [clientMessage, setClientMessage] = useState(existingProposal?.clientMessage ?? '');
  const [validUntil, setValidUntil] = useState(existingProposal?.validUntil ?? '');
  const [pensionType, setPensionType] = useState(String(detail.pensionType ?? ''));
  const [amount, setAmount] = useState(
    detail.recommendedContributionAmount != null ? String(detail.recommendedContributionAmount) : '',
  );
  const [frequency, setFrequency] = useState(String(detail.contributionFrequency ?? 'MONTHLY'));
  const [retirementAge, setRetirementAge] = useState(
    detail.normalRetirementAge != null ? String(detail.normalRetirementAge) : '',
  );
  const [rationale, setRationale] = useState(String(detail.rationale ?? ''));
  const [submitting, setSubmitting] = useState(false);

  const canSubmit =
    (isEditMode || Boolean(targetClientId)) &&
    Boolean(pensionType) &&
    Boolean(amount) &&
    Boolean(frequency) &&
    Boolean(retirementAge);

  const buildDetails = (): Record<string, unknown> => ({
    pensionType,
    recommendedContributionAmount: Number(amount),
    contributionFrequency: frequency,
    currencyCode: 'EUR',
    normalRetirementAge: Number(retirementAge),
    rationale: rationale || undefined,
  });

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      if (isEditMode && existingProposal) {
        const updated = await proposalApi.updateProposal(existingProposal.id, {
          title: title || undefined,
          clientMessage: clientMessage || undefined,
          validUntil: validUntil || undefined,
          details: buildDetails(),
        });
        toast.showToast('Proposal updated', 'success');
        onUpdated?.(updated);
        onClose();
      } else {
        const created = await proposalApi.createProposal({
          profileId,
          targetClientId,
          proposalType: 'PENSION',
          title: title || undefined,
          clientMessage: clientMessage || undefined,
          validUntil: validUntil || undefined,
          details: buildDetails(),
        });
        toast.showToast('Proposal created', 'success');
        onCreated?.(created);
        navigate(`/relationships/${profileId}/proposals/${created.id}`);
      }
    } catch (err) {
      const message = axios.isAxiosError(err)
        ? (err.response?.data as { message?: string })?.message ?? err.message
        : 'Failed to save proposal';
      toast.showToast(message, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const clientOptions = clients.map((c) => ({
    value: c.id,
    text: `${c.firstName} ${c.lastName}`,
  }));
  const pensionTypeOptions = PENSION_TYPE_OPTIONS.map((o) => ({ value: o.value, text: o.label }));
  const frequencyOptions = CONTRIBUTION_FREQUENCY_OPTIONS.map((o) => ({ value: o.value, text: o.label }));

  return (
    <Modal open={open} onClose={onClose} size="large">
      <Modal.Header
        icon={isEditMode ? 'edit' : 'plus square outline'}
        title={isEditMode ? 'Edit Pension Proposal' : 'New Pension Proposal'}
        onClose={onClose}
      />
      <Modal.Content>
        <div data-testid="pension-proposal-form" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {!isEditMode && !isSolo && (
            <Dropdown
              label="Client"
              placeholder="Select a client"
              options={clientOptions}
              value={targetClientId}
              onChange={setTargetClientId}
              aria-label="Client"
            />
          )}
          <TextInput
            label="Title"
            value={title}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTitle(e.target.value)}
          />
          <Dropdown
            label="Pension type"
            placeholder="Select a pension type"
            options={pensionTypeOptions}
            value={pensionType}
            onChange={setPensionType}
            aria-label="Pension type"
          />
          <TextInput
            label="Recommended contribution (EUR)"
            type="number"
            value={amount}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAmount(e.target.value)}
          />
          <Dropdown
            label="Contribution frequency"
            options={frequencyOptions}
            value={frequency}
            onChange={setFrequency}
            aria-label="Contribution frequency"
          />
          <TextInput
            label="Normal retirement age"
            type="number"
            value={retirementAge}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setRetirementAge(e.target.value)}
          />
          <TextInput
            label="Valid until (optional)"
            type="date"
            value={validUntil}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setValidUntil(e.target.value)}
          />
          <TextArea
            label="Message to client"
            value={clientMessage}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setClientMessage(e.target.value)}
            rows={3}
          />
          <TextArea
            label="Rationale"
            value={rationale}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setRationale(e.target.value)}
            rows={3}
          />
        </div>
      </Modal.Content>
      <Modal.Actions
        primaryAction={{
          label: isEditMode ? 'Save changes' : 'Create proposal',
          onClick: handleSubmit,
          disabled: !canSubmit || submitting,
          loading: submitting,
        }}
        secondaryAction={{ label: 'Cancel', onClick: onClose }}
      />
    </Modal>
  );
};

export default NewPensionProposalModal;
